import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Modal } from '@tanium/react-modal';
import { Stack } from '@tanium/react-stack';
import { Field, FieldProps, Form, Formik } from 'formik';
import { Button } from '@tanium/react-button';
import { FormCheckboxInput } from '@tanium/react-form-checkbox-input';
import { HostTargetAccess } from 'gql/HostTargetAccess';
import Helper from 'utils/Helper';
import FormTextField from '../form/formTextField';

type HostTargetAccessForm = {
  adGroups: string;
  hostName: string;
  userName: string;
  graphicalAccess: boolean;
  description: string;
};

const defaultCreateForm: HostTargetAccessForm = {
  adGroups: '',
  hostName: '',
  userName: '',
  graphicalAccess: false,
  description: '',
};

function mapHostTargetAccessToForm({
  adGroups,
  hostName,
  userName,
  graphicalAccess,
  description,
}: HostTargetAccess): HostTargetAccessForm {
  return {
    adGroups: adGroups.join(','),
    hostName,
    userName,
    graphicalAccess,
    description: description || '',
  };
}

export type Operation =
  | {
      type: 'create';
      adGroups: string[];
      hostName: string;
      userName: string;
      graphicalAccess: boolean;
      description?: string;
      targetOrTargetGroupId: string;
    }
  | {
      type: 'update';
      adGroups: string[];
      hostName: string;
      userName: string;
      graphicalAccess: boolean;
      description?: string;
      id: string;
      targetOrTargetGroupId: string;
    }
  | {
      type: 'cancel';
    };

type HostTargetAccessCRUDModalProps = {
  isOpen: boolean;
  onProcess: (operation: Operation) => void;
  hostToEdit?: HostTargetAccess;
  targetOrTargetGroupId: string;
};

const HostTargetAccessCRUDModal: React.VFC<HostTargetAccessCRUDModalProps> = ({
  onProcess,
  hostToEdit,
  targetOrTargetGroupId,
  isOpen,
}) => {
  const { t } = useTranslation();

  const handleSubmit = useCallback(
    (data: HostTargetAccessForm) => {
      const operation: Operation =
        hostToEdit === undefined
          ? {
              type: 'create',
              adGroups: Helper.safeConvertOptionalStringToArray(data.adGroups),
              hostName: data.hostName,
              userName: data.userName,
              graphicalAccess: data.graphicalAccess,
              description: data.description,
              targetOrTargetGroupId,
            }
          : {
              type: 'update',
              adGroups: Helper.safeConvertOptionalStringToArray(data.adGroups),
              hostName: data.hostName,
              userName: data.userName,
              graphicalAccess: data.graphicalAccess,
              description: data.description,
              id: hostToEdit.id,
              targetOrTargetGroupId: hostToEdit.targetOrTargetGroupID,
            };
      onProcess(operation);
    },
    [onProcess, hostToEdit, targetOrTargetGroupId]
  );

  const handleCancel = useCallback(() => {
    onProcess({
      type: 'cancel',
    });
  }, [onProcess]);

  const validationSchema = yup.object().shape({
    hostName: yup.string().label(t('HOST_TARGET_ACCESS_HOSTNAME_LABEL')).required(),
    userName: yup.string().label(t('HOST_TARGET_ACCESS_USERNAME_LABEL')).required(),
    description: yup.string().label(t('DESCRIPTION')).nullable(),
    adGroups: yup.string().label(t('AD_GROUPS')).required(),
    graphicalAccess: yup.string().label(t('HOST_TARGET_ACCESS_GRAPHICAL_ACCESS_LABEL')).required(),
  });

  return (
    <Modal
      isOpen={isOpen}
      size="md"
      onRequestClose={handleCancel}
      header={
        hostToEdit === undefined
          ? t('HOST_TARGET_ACCESS_CREATE_MODAL_HEADER')
          : t('HOST_TARGET_ACCESS_UPDATE_MODAL_HEADER')
      }
    >
      <Formik
        initialValues={hostToEdit !== undefined ? mapHostTargetAccessToForm(hostToEdit) : defaultCreateForm}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form>
          <Stack direction="column" itemSpacing={1}>
            <FormTextField
              label={t('HOST_TARGET_ACCESS_HOSTNAME_LABEL')}
              description={t('HOST_TARGET_ACCESS_HOSTNAME_DESCRIPTION')}
              fieldName="hostName"
              placeholder={t('HOST_TARGET_ACCESS_HOSTNAME_PLACEHOLDER')}
              required
            />
            <FormTextField
              label={t('HOST_TARGET_ACCESS_USERNAME_LABEL')}
              description={t('HOST_TARGET_ACCESS_USERNAME_DESCRIPTION')}
              fieldName="userName"
              placeholder={t('HOST_TARGET_ACCESS_USERNAME_PLACEHOLDER')}
              required
            />
            <FormTextField
              label={t('DESCRIPTION')}
              description={t('HOST_TARGET_ACCESS_DESCRIPTION_DESCRIPTION')}
              fieldName="description"
              placeholder={t('HOST_TARGET_ACCESS_DESCRIPTION_PLACEHOLDER')}
            />
            <FormTextField
              label={t('AD_GROUPS')}
              description={t('AD_GROUPS_DESCRIPTION')}
              fieldName="adGroups"
              placeholder={t('AD_GROUPS_PLACEHOLDER')}
              required
            />
            <Field name="graphicalAccess">
              {({ field }: FieldProps): React.ReactElement => (
                <FormCheckboxInput
                  label={t('HOST_TARGET_ACCESS_GRAPHICAL_ACCESS_LABEL')}
                  data-testid="graphicalAccess"
                  {...field}
                  name={field.name}
                  checked={field.value}
                />
              )}
            </Field>
            <Stack itemSpacing={1}>
              <Button data-testid="submit" type="submit">
                {hostToEdit === undefined ? t('CREATE') : t('UPDATE')}
              </Button>
              <Button type="button" data-testid="cancel" variant="secondary" onClick={handleCancel}>
                {t('CANCEL')}
              </Button>
            </Stack>
          </Stack>
        </Form>
      </Formik>
    </Modal>
  );
};

export default HostTargetAccessCRUDModal;
